import { useState } from "react";
import {
  Check,
  Circle,
  CircleCheck,
  Loader,
  LucideUpload,
  Search,
  Sigma,
  SortAsc,
  X,
  Clock,
  HelpCircle,
  TrendingUp,
  CheckCircle2,
  AlertOctagon,
  Zap,
  Plus,
  ReceiptText,
} from "lucide-react";
import LoanCard from "../../shared/LoanCard";
import Pagination from "../../shared/Pagination";
import { useNavigate } from "react-router-dom";
import { SkeletonLoansPage } from "../../skeletons/LoansSkeletons";
import { useFormatAmount } from "../../hooks/useFormatAmount";
import { useFormattedDate } from "../../hooks/useFormattedDate";
import { useError } from "../../context/ErrorProvider";
import { getLoans, getLoanStatistics } from "../../sdk/loans/loans";
import { useMutation, useQuery } from "react-query";
import LoanFilter from "../../components/filter/LoanFilter";
import Loading from "../../shared/Loading";
import { Button, Dropdown } from "antd";
import { exportAllSelectedLoans, exportLoans } from "../../sdk/exports/export";
import { downloadExportFile } from "../../utils/export";

const Loans = () => {
  const items = [
    {
      key: "csv",
      label: "Export as CSV",
      onClick: () => exportAllLoans({ format: "csv" }),
    },
    {
      key: "pdf",
      label: "Export as PDF",
      onClick: () => exportAllLoans({ format: "pdf" }),
    },
  ];

  const selectedItems = [
    {
      key: "csv",
      label: "Export as CSV",
      onClick: () => exportSelectedLoans({ format: "csv" }),
    },
    {
      key: "pdf",
      label: "Export as PDF",
      onClick: () => exportSelectedLoans({ format: "pdf" }),
    },
  ];
  const handlePageChange = (page) => {
    setPage(page);
  };
  const [totalPages, setTotalPages] = useState(0);
  const [openLoanFilter, setOpenLoanFilter] = useState(false);
  const formatAmount = useFormatAmount();
  const formattedDate = useFormattedDate();
  const { showError } = useError();
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(20);
  const [fromDate, setFromDate] = useState(new Date("2023-11-23"));
  const [toDate, setToDate] = useState(new Date());
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selectedLoanOfficer, setSelectedLoanOfficer] = useState("");
  const [loans, setLoans] = useState([]);
  const [selectedLoans, setSelectedLoans] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [minLoanAmount, setMinLoanAmount] = useState("");
  const [maxLoanAmount, setMaxLoanAmount] = useState("");
  const [selectedLoanProduct, setSelectedLoanProduct] = useState("");
  const [selectedLoanBalance, setSelectedLoanBalance] = useState("");

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedLoans([]);
      setSelectAll(false);
    } else {
      const allLoanIds = loans.map((loan) => loan.id);
      setSelectedLoans(allLoanIds);
      setSelectAll(true);
    }
  };

  const handleRowSelect = (loanId) => {
    if (selectedLoans.includes(loanId)) {
      setSelectedLoans(selectedLoans.filter((id) => id !== loanId));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedLoans, loanId];
      setSelectedLoans(newSelected);
      setSelectAll(newSelected.length === loans.length);
    }
  };

  const navigate = useNavigate();
  const getStatusIcon = (status) => {
    const s = status?.toLowerCase() || "";

    const statusMap = {
      active: {
        color: "text-[#2A8C52]",
        bg: "bg-green-50",
        borderColor: "border-green-100",
        icon: <Zap size={13} />,
        label: "Active",
      },
      pending: {
        color: "text-amber-600",
        bg: "bg-amber-50",
        borderColor: "border-amber-100",
        icon: <Clock size={13} className="animate-pulse" />,
        label: "Pending",
      },
      defaulted: {
        color: "text-rose-600",
        bg: "bg-rose-50",
        borderColor: "border-rose-100",
        icon: <AlertOctagon size={13} />,
        label: "Defaulted",
      },
      paid: {
        color: "text-emerald-700",
        bg: "bg-emerald-100/50",
        borderColor: "border-emerald-200",
        icon: <CheckCircle2 size={13} />,
        label: "Fully Paid",
      },
      repaying: {
        color: "text-blue-600",
        bg: "bg-blue-50",
        borderColor: "border-blue-100",
        icon: <TrendingUp size={13} />,
        label: "Repaying",
      },
    };

    const config = statusMap[s] || {
      color: "text-slate-400",
      bg: "bg-slate-50",
      borderColor: "border-slate-100",
      icon: <HelpCircle size={13} />,
      label: status || "Unknown",
    };

    return (
      <div
        className={`
        inline-flex items-center gap-1.5 
        px-3 py-1 rounded-full 
        font-bold text-[10px] sm:text-[11px] 
        uppercase tracking-wider 
        border shadow-sm transition-all duration-200
        ${config.bg} ${config.color} ${config.borderColor}
      `}
      >
        <span className="flex-shrink-0">{config.icon}</span>
        <span className="whitespace-nowrap">{config.label}</span>
      </div>
    );
  };

  const toggleOpenLoanFilter = () => {
    setOpenLoanFilter(true);
  };
  const closeOpenLoanFilter = () => {
    setOpenLoanFilter(false);
  };

  const handleApplyFilter = () => {
    closeOpenLoanFilter();
  };

  const {
    refetch: refetchLoans,
    isRefetching,
    isLoading,
  } = useQuery({
    queryKey: [
      "loan",
      page,
      limit,
      fromDate ? fromDate.toISOString() : null,
      toDate ? toDate.toISOString() : null,
      status,
      selectedLoanOfficer,
      selectedLoanProduct,
      minLoanAmount,
      maxLoanAmount,
    ],
    queryFn: async () => {
      const response = await getLoans(
        page,
        limit,
        fromDate ? fromDate.toISOString() : null,
        toDate ? toDate.toISOString() : null,
        search,
        status,
        selectedLoanOfficer,
        selectedLoanProduct,
        minLoanAmount,
        maxLoanAmount,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      setLoans(data.data);
      setTotalPages(data?.totalPages);
      setPage(data.currentPage);
      setLimit(data.limit);
      setTotalItems(data?.totalItems);
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  useQuery({
    queryKey: [],
  });

  const { data: loanStatistics } = useQuery({
    queryKey: ["loan-statistics"],
    queryFn: async () => {
      const response = await getLoanStatistics();
      return response.data.data;
    },
  });

  const { mutate: exportAllLoans, isLoading: loadingExportLoans } = useMutation(
    {
      mutationKey: ["export all loans"],
      mutationFn: ({ format }) =>
        exportLoans(
          format,
          fromDate,
          toDate,
          search,
          status,
          minLoanAmount,
          maxLoanAmount,
          selectedLoanProduct,
          selectedLoanOfficer,
        ),
      onSuccess: (blob, variables) => {
        downloadExportFile(blob, `Loans.${variables.format}`);
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    },
  );

  const { mutate: exportSelectedLoans, isLoading: loadingExportSelectedLoans } =
    useMutation({
      mutationKey: ["export all selected loans"],
      mutationFn: ({ format }) => exportAllSelectedLoans(format, selectedLoans),
      onSuccess: (blob, variables) => {
        downloadExportFile(blob, `Loans.${variables.format}`);
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalItems);

  return (
    <>
      {openLoanFilter && (
        <LoanFilter
          closeFilters={closeOpenLoanFilter}
          handleApplyFilter={handleApplyFilter}
          status={status}
          setStatus={setStatus}
          selectedLoanOfficer={selectedLoanOfficer}
          setSelectedLoanOfficer={setSelectedLoanOfficer}
          selectedLoanProduct={selectedLoanProduct}
          setSelectedLoanProduct={setSelectedLoanProduct}
          selectedLoanBalance={selectedLoanBalance}
          setSelectedLoanBalance={setSelectedLoanBalance}
          minLoanAmount={minLoanAmount}
          setMinLoanAmount={setMinLoanAmount}
          maxLoanAmount={maxLoanAmount}
          setMaxLoanAmount={setMaxLoanAmount}
          fromDate={fromDate}
          toDate={toDate}
          setFromDate={setFromDate}
          setToDate={setToDate}
        />
      )}
      {isLoading ? (
        <SkeletonLoansPage />
      ) : (
        <div className="pl-[10px]">
          <div className="h-[50px] flex items-center justify-between">
            <p className="font-bold text-[28px] text-[#222222]">Overview</p>
          </div>
          <div className="flex items-center mb-[20px] justify-between">
            <LoanCard
              icon={<Sigma />}
              label="Total Loans"
              value={loanStatistics?.total ?? 0}
              increment="+0.00%"
              padColor="#4B5D67"
            />
            <LoanCard
              icon={<Circle color="#00A676" />}
              label="Active Loans"
              value={loanStatistics?.active ?? 0}
              increment="+0.00%"
              padColor="#00A676"
            />
            <LoanCard
              icon={<CircleCheck color="#F4A261" />}
              label="Awaiting Disbursement"
              value={loanStatistics?.pending ?? 0}
              increment="-0.00%"
              padColor="#F4A261"
            />
            <LoanCard
              icon={<X color="#000000" />}
              label="Defaulted Loans"
              value={loanStatistics?.defaulted ?? 0}
              increment="+0.00%"
              type="negative"
              padColor="#E63946"
            />
            <LoanCard
              icon={<Check color="#ADB5BD" />}
              label="Paid-off Loans"
              value={loanStatistics?.["closed"] ?? 0}
              increment="-0.00%"
              type="negative"
              padColor="#ADB5BD"
            />
          </div>
          <div className="h-[70px] w-full bg-white flex items-center justify-between px-[10px]">
            <button
              onClick={refetchLoans}
              className="h-[40px] w-[117px] flex items-center justify-center gap-[5px] border border-[#00A676] bg-white rounded-[10px] text-[#00A676] font-medium text-[15px]"
            >
              {isRefetching ? <Loading /> : <Loader size={17} />}
              Update
            </button>
            <div className="h-[40px] w-[117px] text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-white flex items-center justify-center">
              {selectedLoans?.length} Selected
            </div>
            <div
              onClick={toggleOpenLoanFilter}
              className="h-[40px] cursor-pointer w-[127px] text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-[#FBF5F5] flex items-center justify-center gap-[5px]"
            >
              <SortAsc color="#000000" />
              <span className="text-[#000000]">Filter</span>
              <span className="text-[#000000]">0</span>
            </div>
            <div className="flex items-center border border-gray-300 rounded-md px-2 h-[40px] w-[50%] bg-white">
              <Search className="text-gray-500 w-5 h-5 mr-2" />
              <input
                type="text"
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search loans, name, loan code..."
                className="w-full outline-none text-sm text-gray-700 placeholder-gray-400 bg-transparent"
              />
              <button
                onClick={refetchLoans}
                className="ml-2 p-2 bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <Search className="w-4 h-4 text-white" />
              </button>
            </div>
            <p className="text-[#000000] truncate text-[15px] font-normal">
              {loans?.length ?? 0} Results
            </p>
            {selectedLoans?.length === 0 ? (
              <Dropdown menu={{ items }} trigger={["click"]} placement="bottom">
                <Button className="flex items-center bg-[#F3F3F3] border-[0.7px] border-[#A8A8A8] h-[40px] rounded-[30px] px-[20px] justify-center gap-[5px] text-[16px] text-[#5C5C5C]">
                  {loadingExportLoans ? (
                    <Loading />
                  ) : (
                    <LucideUpload size={17} />
                  )}
                  <span>Export</span>
                </Button>
              </Dropdown>
            ) : (
              <Dropdown
                menu={{ items: selectedItems }}
                trigger={["click"]}
                placement="bottom"
              >
                <Button className="flex items-center bg-[#F3F3F3] border-[0.7px] border-[#A8A8A8] h-[40px] rounded-[30px] px-[20px] justify-center gap-[5px] text-[16px] text-[#5C5C5C]">
                  {loadingExportSelectedLoans ? (
                    <Loading />
                  ) : (
                    <LucideUpload size={17} />
                  )}
                  <span>Export</span>
                </Button>
              </Dropdown>
            )}
          </div>
          <div>
            <div className="h-[44px] px-[10px] w-full bg-[#F3F3F3] mb-[10px] flex items-center">
              <div className="w-[3%]">
                <input
                  type="checkbox"
                  name="loan"
                  id="loan"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
              </div>
              <div className="w-[5%] text-[#000] font-bold text-[14px]">
                LOAN ID
              </div>
              <div className="w-[15%] text-[#000] truncate font-bold text-[14px]">
                BORROWER’S NAME
              </div>
              <div className="w-[10%] text-[#000] font-bold text-[14px]">
                LOAN TYPE
              </div>
              <div className="w-[10%] text-[#000] font-bold text-[14px]">
                LOAN PERIOD
              </div>
              <div className="w-[11%] text-[#000] font-bold text-[14px]">
                LOAN AMOUNT
              </div>
              <div className="w-[11%] text-[#000] font-bold text-[14px]">
                BALANCE
              </div>
              <div className="w-[10%] text-[#000] font-bold text-[14px]">
                DISBURSE DATE
              </div>
              <div className="w-[10%] text-[#000] font-bold text-[14px]">
                DUE DATE
              </div>
              <div className="w-[15%] text-[#000] font-bold text-[14px]">
                STATUS
              </div>
            </div>
            {loans?.length > 0 ? (
              loans?.map((loan, index) => (
                <div
                  key={index}
                  className="h-[35px] px-[10px] w-full bg-[#FFFFFF] mb-[7px] flex items-center"
                >
                  <div className="w-[3%]">
                    <input
                      type="checkbox"
                      name="loan"
                      checked={selectedLoans.includes(loan.id)}
                      onChange={() => handleRowSelect(loan.id)}
                    />
                  </div>
                  <div
                    onClick={() => navigate(`/dashboard/loans/${loan?.id}`)}
                    className="w-[5%] text-[#2A8C52] cursor-pointer font-normal text-[13px]"
                  >
                    {loan.public_id}
                  </div>
                  <div className="w-[15%] truncate text-[#000] font-normal text-[13px]">
                    {loan?.loanApplication?.customer?.firstname}{" "}
                    {loan?.loanApplication?.customer?.lastname}
                  </div>
                  <div className="w-[10%] truncate text-[#000] font-normal text-[13px]">
                    {loan?.loanApplication?.product?.name ?? "N/A"}
                  </div>
                  <div className="w-[10%] text-[#000] font-normal text-[13px]">
                    {loan?.loanApplication?.loanPeriod} months
                  </div>
                  <div className="w-[11%] text-[#000] font-normal text-[13px]">
                    {formatAmount(loan?.loanApplication?.loanAmount ?? 0)}
                  </div>
                  <div className="w-[11%] text-[#000] font-normal text-[13px]">
                    {loan?.status === "pending"
                      ? "N/A"
                      : formatAmount(loan?.balance ?? 0)}
                  </div>
                  <div className="w-[10%] text-[#000] font-normal text-[13px]">
                    {formattedDate(loan?.dueDate)}
                  </div>
                  <div className="w-[10%] text-[#000] font-normal text-[13px]">
                    {formattedDate(loan?.disbursedAt)}
                  </div>
                  <div className="w-[15%] truncate text-[#000] font-normal text-[13px] flex items-center gap-2">
                    {getStatusIcon(loan?.status)}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-24 px-6 bg-white border-2 border-gray-100 transition-all">
                {/* Icon Container with soft ZipGo Green glow */}
                <div className="relative mb-8">
                  <div className="absolute inset-0 bg-[#2A8C52] blur-[40px] opacity-10 rounded-full scale-150" />

                  <div className="relative w-24 h-24 bg-white rounded-3xl flex items-center justify-center border border-gray-100 shadow-2xl shadow-gray-200/40">
                    {/* ReceiptText or Landmark works great for Loans */}
                    <ReceiptText
                      size={40}
                      className="text-[#2A8C52] opacity-90"
                    />

                    {/* Floating 'plus' badge */}
                    <div className="absolute -top-2 -right-2 w-9 h-9 bg-[#2A8C52] rounded-2xl flex items-center justify-center border-4 border-white shadow-lg">
                      <Plus size={18} className="text-white" strokeWidth={3} />
                    </div>
                  </div>
                </div>

                {/* Textual Information */}
                <div className="max-w-md text-center">
                  <h3 className="text-gray-900 font-extrabold text-2xl tracking-tight">
                    No Loan Records Found
                  </h3>
                  <p className="text-gray-500 mt-3 text-[15px] leading-relaxed">
                    Your loan directory is currently empty. You can start by
                    issuing a new loan to an existing borrower or adjust your
                    current search filters.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="mt-10 flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
                  <button className="sm:w-auto px-8 py-3.5 bg-[#2A8C52] text-white text-sm font-bold rounded-2xl hover:bg-[#227042] hover:shadow-xl hover:shadow-green-900/20 transition-all flex items-center justify-center gap-2 group">
                    <Plus
                      size={18}
                      className="group-hover:rotate-90 transition-transform duration-300"
                    />
                    Issue New Loan
                  </button>

                  <button className="sm:w-auto px-8 py-3.5 bg-white text-gray-600 text-sm font-bold rounded-2xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center gap-2 shadow-sm">
                    <Search size={18} />
                    Reset Filters
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="h-[50px] mb-[20px] flex items-center justify-between">
            <div className="flex items-center gap-[10px]">
              <p className="text-[15px] text-[#5C5C5C] font-normal">
                {start}–{end} of {totalItems}
              </p>
              <p className="text-[15px] text-[#5C5C5C] font-normal">
                Row/Page:
              </p>
              <p className="text-[15px] text-[#5C5C5C] font-normal">
                {page}/{totalPages}
              </p>
              <select
                className="h-[35px] w-[100px] px-[10px] text-[12px]"
                name="row"
                id="row"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
              >
                <option value="10">10 items</option>
                <option value="20">20 items</option>
                <option value="25">25 items</option>
              </select>
            </div>
            <div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Loans;
